import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Rocket, TrendingUp, Expand, Check } from 'lucide-react';

// Every text of a plan is admin:landing.pricing.plans.<id>.<field>, and each
// feature admin:landing.pricing.plans.<id>.features.<key>.
const plans = [
  {
    id: 'sprint',
    color: 'from-purple-500 to-fuchsia-500',
    icon: Rocket,
    features: ['setup', 'script', 'proof'],
  },
  {
    id: 'croissance',
    color: 'from-blue-500 to-cyan-500',
    icon: TrendingUp,
    features: ['data', 'abTesting', 'crm', 'sdr'],
  },
  {
    id: 'expansion',
    color: 'from-rose-500 to-pink-500',
    icon: Expand,
    features: ['team', 'goToMarket', 'sequences', 'followUps'],
  },
];

export default function PricingSection() {
  const { t } = useTranslation(['admin']);
  return (
    <section id="pricing" className="relative py-32 bg-[#0a0a0a]">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-purple-400 mb-4 block">{t('admin:landing.pricing.label')}</span>
          <h2 className="text-3xl md:text-5xl font-bold text-white">{t('admin:landing.pricing.heading')}</h2>
          <p className="text-white/40 mt-4 max-w-2xl mx-auto">{t('admin:landing.pricing.sectionSubtitle')}</p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan, idx) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.08 }}
              className={`relative h-full p-6 rounded-2xl ${plan.id === 'expansion' ? 'bg-gradient-to-br from-rose-500/10 via-fuchsia-500/10 to-pink-500/10' : 'bg-white/[0.02]'} border border-white/[0.05] hover:border-white/10 transition-all duration-300 flex flex-col` }
            >
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${plan.color} flex items-center justify-center mb-4`}>
                <plan.icon className="w-5 h-5 text-white" />
              </div>
              <div className="flex items-center gap-2 mb-2 md:min-h-[28px]">
                <h3 className="text-lg font-semibold text-white">{t(`admin:landing.pricing.plans.${plan.id}.name`)}</h3>
                <span className="text-xs px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/60">{t(`admin:landing.pricing.plans.${plan.id}.tag`)}</span>
              </div>
              <p className="text-white/60 text-sm leading-relaxed mb-4 md:min-h-[48px]">{t(`admin:landing.pricing.plans.${plan.id}.headline`)}</p>

              <div className="mb-4 md:min-h-[72px]">
                <div className="text-3xl font-bold text-white">{t(`admin:landing.pricing.plans.${plan.id}.price`)} <span className="text-base font-medium text-white/50">{t(`admin:landing.pricing.plans.${plan.id}.cadence`)}</span></div>
                <div className="text-sm text-white/60 mt-1">{t(`admin:landing.pricing.plans.${plan.id}.highlight`)}</div>
              </div>

              <div className="text-sm text-white/70 mb-4 md:min-h-[28px] leading-snug">
                <span className="inline-flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-400" /> {t(`admin:landing.pricing.plans.${plan.id}.deliverable`)}
                </span>
              </div>

              <ul className="space-y-2 mt-auto">
                {plan.features.map((f) => (
                  <li key={f} className="text-sm text-white/60 flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 text-white/40" />
                    <span>{t(`admin:landing.pricing.plans.${plan.id}.features.${f}`)}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}